import { useAppDispatch, useAppSelector } from "@hooks";
import React, { PropsWithChildren, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { generateAuthenticationUrl } from "@services";
import { constantTimeCompare } from "@services/crypto";
import { selectCodeChallenge, selectNonce, selectStatus, selectError } from "@slices";

import { Redirect } from "@components/Redirect/Redirect";
import { exchangeCodeForToken, initializeAuthentication, ZettleAuthStatus } from "@slices";

import { Loading } from "@components";
import { ErrorCategory, ErrorSeverity, createError, logError } from '@services/errorHandling';

export const ZettleAuth = (props: PropsWithChildren) => {
    const { children } = props;
    const dispatch = useAppDispatch();
    const status = useAppSelector(selectStatus);
    const nonce = useAppSelector(selectNonce);
    const error = useAppSelector(selectError);
    const codeChallenge = useAppSelector(selectCodeChallenge);
    const searchParams = useSearchParams()[0];
    const providedNonce = searchParams.get("state");
    const code = searchParams.get("code");

    // Only validate nonce if we have a code from OAuth redirect
    // Use constant-time comparison to prevent timing attacks on CSRF token
    const validNonce = code ? constantTimeCompare(nonce, providedNonce) : true;

    useEffect(() => {
        switch (status) {
            case ZettleAuthStatus.Unauthenticated:
                dispatch(initializeAuthentication());
                break;
            case ZettleAuthStatus.WaitingForCode:
                if (code && validNonce) dispatch(exchangeCodeForToken(code));
                break;
        }
    }, [status, dispatch, code, validNonce]);

    if (status === ZettleAuthStatus.Authenticated) {
        if(code) {
            const location = new URL(window.location.href);
            location.search = "";
            return <Redirect to={location}/>
        }
        return <>{children}</>;
    }

    if (status === ZettleAuthStatus.Error) {
        const errorMessage = error || "Authentication error occurred";
        throw new Response(errorMessage, { status: 400 });
    }

    if (status === ZettleAuthStatus.WaitingForCode) {
        // if a code isn't provided, redirect to Zettle
        if (!code) {
            if (process.env.NODE_ENV === 'development') {
                console.log("No OAuth code, redirecting to Zettle for authentication");
            }
            if (!codeChallenge || !nonce) {
                return <Loading />;
            }
            return <Redirect to={generateAuthenticationUrl(codeChallenge, nonce, window.location.href)} />;
        }

        // Validate nonce only when we have a code
        if (!validNonce) {
            // SECURITY: Potential CSRF attack detected
            // The state parameter returned from OAuth doesn't match our stored nonce
            const csrfError = createError(
                ErrorCategory.SECURITY,
                ErrorSeverity.CRITICAL,
                "CSRF validation failed - state parameter mismatch (potential security attack)",
                new Error('State parameter mismatch'),
                {
                    expectedNonce: nonce,
                    receivedState: providedNonce,
                    hasCode: !!code
                }
            );
            logError(csrfError);

            // Clear the URL parameters to prevent retry with invalid state
            const location = new URL(window.location.href);
            location.search = "";

            // Re-initialize authentication with new nonce
            dispatch(initializeAuthentication());

            // In production, this might indicate a CSRF attack
            throw new Response("CSRF validation failed. Please try authenticating again.", { status: 403 });
        }
    }

    return <Loading />;
};
